import { Capacitor, registerPlugin } from '@capacitor/core';
import type { PagerState } from './pocket-pager';

// Web bridge to the native GameClockPlugin: the live-game Live Activity on the
// Lock Screen / Dynamic Island. It carries the game clock countdown and the
// Pocket Pager (inning, pitcher, pitch count vs limit, next inning).
//
// Everything here is best-effort: the live game screen is the source of truth
// and the Live Activity is a glanceable mirror. All calls no-op on web and on
// iOS versions / devices that don't support Live Activities. Every call sends
// the whole state; native keeps nothing between calls.

export interface GameActivityState extends Partial<PagerState> {
  endsAt?: string | null;   // ISO timestamp the game clock ends at; null/absent = no clock
}

interface GameClockPluginDef {
  startActivity(opts: GameActivityState & { teamName: string; opponent: string; isHome: boolean }): Promise<void>;
  updateActivity(opts: GameActivityState): Promise<void>;
  endActivity(): Promise<void>;
  isAvailable(): Promise<{ available: boolean }>;
}

const GameClockPlugin = registerPlugin<GameClockPluginDef>('GameClockPlugin');

// Capacitor serializes undefined/null awkwardly across the bridge; send only
// the fields that have a value so native reads "absent" as nil.
const clean = <T extends Record<string, any>>(o: T): T =>
  Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== null && v !== '')) as T;

export async function startGameActivity(opts: GameActivityState & {
  teamName: string;
  opponent: string;
  isHome: boolean;
}): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const { available } = await GameClockPlugin.isAvailable();
    if (!available) return;
    await GameClockPlugin.startActivity(clean(opts));
  } catch {
    /* best-effort: the live game screen still works */
  }
}

export async function updateGameActivity(state: GameActivityState): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await GameClockPlugin.updateActivity(clean(state));
  } catch {
    /* best-effort */
  }
}

export async function endGameActivity(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await GameClockPlugin.endActivity();
  } catch {
    /* best-effort */
  }
}
